import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { api, LOGO_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LogOut, Settings, LayoutDashboard, Plus, Users, Inbox, BarChart3 } from "lucide-react";

export const AdminLayout = ({ children, title }) => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [config, setConfig] = useState(null);
  const [newLeads, setNewLeads] = useState(0);

  useEffect(() => {
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
    api.get("/revival-leads/summary").then((r) => setNewLeads(r.data.new || 0)).catch(() => {});
  }, [location.pathname]);

  const navItem = (to, icon, label, badge) => (
    <Link
      to={to}
      data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
      className={`relative flex items-center gap-2 px-2.5 py-2 text-sm tracking-wide whitespace-nowrap transition-colors duration-200 ${
        location.pathname === to || (to !== "/admin" && location.pathname.startsWith(to)) ? "text-[#c19a6b]" : "text-white/70 hover:text-white"
      }`}
    >
      {icon}
      <span className="hidden sm:inline">{label}</span>
      {badge > 0 && (
        <span data-testid={`nav-${label.toLowerCase()}-badge`} className="min-w-[18px] h-[18px] px-1 rounded-full bg-[#c19a6b] text-white text-[10px] font-bold flex items-center justify-center">
          {badge}
        </span>
      )}
    </Link>
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <header className="bg-black text-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <Link to="/admin" data-testid="admin-logo-link" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="ARK Furniture" className="h-9 w-auto bg-white rounded-sm p-1" />
              <span className="font-display text-lg tracking-[0.15em] hidden xl:inline whitespace-nowrap">REVIVAL QUOTES</span>
            </Link>
            {config?.square_environment === "sandbox" && (
              <span
                data-testid="sandbox-mode-badge"
                className="hidden lg:inline bg-[#c19a6b] text-white text-[10px] tracking-[0.2em] uppercase font-bold py-1 px-2.5 whitespace-nowrap"
              >
                Square Sandbox Mode
              </span>
            )}
          </div>
          <nav className="flex items-center gap-1">
            {navItem("/admin", <LayoutDashboard size={16} />, "Dashboard")}
            {navItem("/admin/leads", <Inbox size={16} />, "Leads", newLeads)}
            {navItem("/admin/analytics", <BarChart3 size={16} />, "Analytics")}
            {navItem("/admin/quotes/new", <Plus size={16} />, "New Quote")}
            {navItem("/admin/realtors", <Users size={16} />, "Partners")}
            {navItem("/admin/settings", <Settings size={16} />, "Settings")}
            <button
              data-testid="logout-btn"
              onClick={() => {
                logout();
                navigate("/login");
              }}
              className="flex items-center gap-2 px-3 py-2 text-sm text-white/70 hover:text-white transition-colors duration-200"
            >
              <LogOut size={16} />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </nav>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
        {title && <h1 className="font-display text-3xl sm:text-4xl mb-8 tracking-tight">{title}</h1>}
        {children}
      </main>
    </div>
  );
};
